import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Gift, ArrowRight, Lock, Check, Sparkles, Ticket } from "lucide-react";
import { getCouponOfDay } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ExpiryBadge } from "@/components/CouponDetails";

const AFTER_AUTH_KEY = "readoo_after_auth";
export const COD_TARGET = "/coupon?offerta=oggi";

export const setAfterAuth = (path) => sessionStorage.setItem(AFTER_AUTH_KEY, JSON.stringify({ path, at: Date.now() }));
export const takeAfterAuth = () => {
  try {
    const v = JSON.parse(sessionStorage.getItem(AFTER_AUTH_KEY) || "null");
    sessionStorage.removeItem(AFTER_AUTH_KEY);
    return v && Date.now() - v.at < 30 * 60 * 1000 ? v.path : null;
  } catch { return null; }
};

export const storeName = (n = "") => n.replace(/[_\s-]+IT$/i, "").trim();

const cache = {};
export function useCouponOfDay() {
  const { user, loading } = useAuth();
  const { loginEnabled, loaded } = useSettings();
  const key = user ? `u-${user.user_id}` : "anon";
  const [offer, setOffer] = useState(cache[key] ?? null);
  useEffect(() => {
    if (loading || !loaded || !loginEnabled) return;
    let alive = true;
    (cache[`p-${key}`] ||= getCouponOfDay().then((r) => (cache[key] = r.offer || null)).catch(() => null))
      .then((o) => alive && setOffer(o));
    return () => { alive = false; };
  }, [key, loading, loaded, loginEnabled]);
  return loginEnabled ? offer : null;
}

const todayLabel = () => new Date().toLocaleDateString("it-IT", { day: "numeric", month: "long", timeZone: "Europe/Rome" });

export function CouponOfDayBox({ square = false, className = "", testid = "cod-box" }) {
  const offer = useCouponOfDay();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  if (!offer) return null;

  const onCta = () => (user ? navigate(COD_TARGET) : setOpen(true));
  const row = square ? "flex-col" : "flex-col md:flex-row md:items-center";

  return (
    <>
      <div data-testid={testid} className={`relative overflow-hidden rounded-2xl border border-brand/30 bg-brandTertiary/30 ${className}`}>
        <span aria-hidden className="absolute -right-6 -top-6 w-28 h-28 rounded-full bg-brand/10" />
        <div className={`relative flex gap-5 p-5 sm:p-6 ${row}`}>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-brand text-onBrand font-mono text-[10px] font-bold uppercase tracking-wider">
                <Gift size={12} /> Offerta del giorno
              </span>
              <span className="font-mono text-[11px] text-subtle">{todayLabel()}</span>
            </div>
            <p className="font-mono text-[11px] font-bold uppercase tracking-wider text-brand mb-1">{storeName(offer.advertiser_name)}</p>
            <h3 data-testid={`${testid}-headline`} className="font-serif text-2xl sm:text-[1.7rem] font-semibold leading-tight text-onSurface">{offer.headline}</h3>
            <div className="mt-3"><ExpiryBadge end={offer.end_date} id={`${testid}-expiry`} /></div>
          </div>
          <div className={`flex flex-col gap-2.5 shrink-0 ${square ? "" : "md:w-60"}`}>
            <div data-testid={`${testid}-code`} className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border border-dashed border-brand/60 bg-surface/60 font-mono text-base font-bold tracking-[0.2em] text-brand">
              {user ? <Ticket size={15} /> : <Lock size={14} />}
              <span className="truncate">{user ? offer.voucher_code : offer.code_masked}</span>
            </div>
            <button
              type="button"
              onClick={onCta}
              data-testid={`${testid}-cta`}
              className="group inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-brand text-onBrand text-sm font-bold hover:bg-brandSecondary transition-colors"
            >
              {user ? "Vedi il codice" : "Sblocca il codice"}
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
            </button>
          </div>
        </div>
      </div>
      {!user && <SignupDialog offer={offer} open={open} onOpenChange={setOpen} />}
    </>
  );
}

const PERKS = ["Il codice completo dell'offerta del giorno", "Ogni giorno un nuovo sconto selezionato", "Coupon e codici sconto dei migliori negozi"];

function SignupDialog({ offer, open, onOpenChange }) {
  const navigate = useNavigate();
  const go = (path) => { setAfterAuth(COD_TARGET); onOpenChange(false); navigate(path); };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="cod-signup-dialog" className="bg-surfaceSecondary border-hair text-onSurface sm:rounded-2xl max-w-md">
        <span className="inline-flex self-start items-center gap-1.5 px-2.5 py-1 rounded-full bg-brand/15 text-brand font-mono text-[10px] font-bold uppercase tracking-wider">
          <Sparkles size={12} /> Offerta del giorno
        </span>
        <DialogTitle className="font-serif text-2xl font-semibold leading-tight">Iscriviti gratis per riscattare l'offerta del giorno</DialogTitle>
        <DialogDescription className="text-sm text-onSurfaceSecondary">
          <span className="font-semibold text-onSurface">{offer.headline}</span> — bastano 30 secondi e il codice è tuo.
        </DialogDescription>
        <ul className="space-y-2">
          {PERKS.map((p) => (
            <li key={p} className="flex items-start gap-2 text-sm text-onSurface"><Check size={16} className="text-brand mt-0.5 shrink-0" /> {p}</li>
          ))}
        </ul>
        <button type="button" onClick={() => go("/registrati")} data-testid="cod-signup-register" className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-brand text-onBrand text-sm font-bold hover:bg-brandSecondary transition-colors">
          Iscriviti gratis <ArrowRight size={16} />
        </button>
        <button type="button" onClick={() => go("/login")} data-testid="cod-signup-login" className="text-sm text-subtle hover:text-brand transition-colors">
          Hai già un account? <span className="font-semibold text-brand">Accedi</span>
        </button>
      </DialogContent>
    </Dialog>
  );
}
